import { useMemo } from "react";
import { usePermissionsContext } from "@/auth/PermissionsProvider";
import { type Permission, FULL_ACCESS, permissionMatches } from "@/constants/permissions";

/**
 * Access control is permission-based only. Permissions are colon-namespaced
 * `Module:Action` codes (`Reservations:Read`, `Travel:Deals:Write`) and come from the
 * backend via `myPermissions`. Roles are not consulted - they only exist server-side as
 * a convenience for granting a preset of permissions.
 *
 * Wildcards grant everything below them: `Reservations:*` covers `Reservations:Read`,
 * and `*` covers everything.
 */
export function usePermissions() {
  const { permissions, isLoading } = usePermissionsContext();

  const grantedPermissions = useMemo(() => new Set(permissions), [permissions]);

  const hasPermission = (permission: Permission | string): boolean => {
    for (const granted of grantedPermissions) {
      if (permissionMatches(granted, permission)) {
        return true;
      }
    }

    return false;
  };

  const hasAllPermissions = (required: (Permission | string)[]): boolean =>
    required.every((permission) => hasPermission(permission));

  const hasAnyPermission = (required: (Permission | string)[]): boolean =>
    required.length === 0 || required.some((permission) => hasPermission(permission));

  /**
   * True when the user holds any permission under a namespace, e.g. `Reservations:`.
   * Used to decide whether a whole sidebar group should be shown.
   */
  const hasAnyPermissionWithPrefix = (prefix: string): boolean => {
    if (grantedPermissions.has(FULL_ACCESS)) {
      return true;
    }

    const namespace = prefix.replace(/:$/, "");

    for (const granted of grantedPermissions) {
      if (granted === `${namespace}:${FULL_ACCESS}` || granted.startsWith(`${namespace}:`)) {
        return true;
      }
    }

    return false;
  };

  return {
    permissions: grantedPermissions,
    isLoading,
    hasNoPermissions: !isLoading && grantedPermissions.size === 0,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasAnyPermissionWithPrefix,
  };
}
